import { HttpErrorResponse } from '@angular/common/http';
import { GridApi } from 'ag-grid-community';
import { ConfirmationService, MenuItem } from 'primeng/api';

export const LIMITE_REGISTROS_EXPORTACAO = 5_000_000;
export const EXPORT_ERROR_CODE_LIMITE_EXCEDIDO = 'EXPORT_LIMITE_EXCEDIDO';

export interface ExportacaoErro {
  error: string;
  error_code: typeof EXPORT_ERROR_CODE_LIMITE_EXCEDIDO;
  total: number;
  limite: number;
}

type FormatarNumero = (valor: number) => string;

function formatar(valor: number, formatarNumero?: FormatarNumero): string {
  return formatarNumero?.(valor) ?? String(valor);
}

/**
 * Abre o modal de bloqueio informando o excesso de volume e orientando o refinamento
 * dos filtros. Não oferece a opção de prosseguir com a exportação.
 */
export function mostrarBloqueioLimiteExportacao(opts: {
  confirmationService: ConfirmationService;
  totalLinhas: number;
  limite?: number;
  formatarNumero?: FormatarNumero;
}): void {
  const total = formatar(opts.totalLinhas, opts.formatarNumero);
  const limite = formatar(opts.limite ?? LIMITE_REGISTROS_EXPORTACAO, opts.formatarNumero);

  opts.confirmationService.confirm({
    header: 'Limite de exportação excedido',
    message:
      `A sua pesquisa retornou ${total} registros, acima do limite máximo de ${limite} registros permitidos por exportação. ` +
      'Refine os filtros para diminuir o volume de dados e tente novamente.',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Entendi',
    rejectVisible: false,
  });
}

/**
 * Ponto único de entrada da exportação assíncrona, reaproveitável por qualquer tela
 * paginada: bloqueia quando o total da consulta excede o limite de registros e, caso
 * contrário, pede a confirmação habitual antes de disparar a solicitação.
 */
export function confirmarExportacaoAssincrona(opts: {
  confirmationService: ConfirmationService;
  totalLinhas: number;
  totalColunas: number;
  formatarNumero?: FormatarNumero;
  onConfirm: () => void;
}): void {
  if (opts.totalLinhas > LIMITE_REGISTROS_EXPORTACAO) {
    mostrarBloqueioLimiteExportacao(opts);
    return;
  }

  const totalLinhas = formatar(opts.totalLinhas, opts.formatarNumero);

  opts.confirmationService.confirm({
    header: 'Confirmação de Exportação',
    message:
      `O arquivo será gerado com ${opts.totalColunas} colunas e ${totalLinhas} linhas. Você receberá uma notificação no seu e-mail quando o arquivo estiver pronto para ser baixado.` +
      '<br><br>' +
      'Para confirmar, clique em "Exportar".',
    icon: 'pi pi-exclamation-triangle',
    // rotulos que antes vinham do <p-confirmDialog> proprio de cada tela; hoje quem
    // renderiza e o dialogo unico do AppComponent, sem template de footer
    acceptLabel: 'Exportar',
    rejectLabel: 'Cancelar',
    accept: opts.onConfirm,
  });
}

/**
 * Trata o erro da solicitação de exportação. Quando a API recusou por excesso de volume,
 * abre o mesmo modal de bloqueio (com o total que a API contou) e devolve true; nos demais
 * casos devolve false para que a tela siga com o tratamento padrão de erro.
 */
export function tratarErroLimiteExportacao(opts: {
  confirmationService: ConfirmationService;
  erro: unknown;
  formatarNumero?: FormatarNumero;
}): boolean {
  const corpo = opts.erro instanceof HttpErrorResponse ? (opts.erro.error as Partial<ExportacaoErro> | null) : null;

  if (corpo?.error_code !== EXPORT_ERROR_CODE_LIMITE_EXCEDIDO) {
    return false;
  }

  mostrarBloqueioLimiteExportacao({
    confirmationService: opts.confirmationService,
    totalLinhas: corpo.total ?? 0,
    limite: corpo.limite,
    formatarNumero: opts.formatarNumero,
  });
  return true;
}

export const MOTIVO_BLOQUEIO_VISAO_AGRUPADA = 'A exportação assíncrona não está disponível para visões agrupadas.';

/**
 * Indica se o grid está em uma "visão agrupada" — modo pivot ou com colunas de
 * agrupamento de linhas.
 */

export function isVisaoAgrupada(api: GridApi): boolean {
  if (!api) {
    return false;
  }

  if (api.isPivotMode() || api.getRowGroupColumns().length > 0) {
    return true;
  }

  return api.getAllDisplayedColumns().some(col => !col.getColDef().field);
}

export interface MenuExportacaoOptions {
  onExportarLinhasRetornadas: () => void;

  onExportarTodas: () => void;

  assincronaVisivel?: boolean;

  assincronaDesabilitada?: boolean;

  motivoBloqueioAssincrona?: string | null;
}

export function buildMenuExportacao(opts: MenuExportacaoOptions): MenuItem[] {
  const motivoBloqueio = opts.motivoBloqueioAssincrona ?? null;

  return [
    {
      label: 'Exportar listagens',
      items: [
        {
          label: 'Linhas retornadas',
          icon: 'pi pi-file-excel',
          command: () => opts.onExportarLinhasRetornadas(),
        },
        {
          label: 'Todas as pesquisadas',
          icon: 'pi pi-file-excel',
          visible: opts.assincronaVisivel ?? true,
          disabled: opts.assincronaDesabilitada || !!motivoBloqueio,
          tooltipOptions: { tooltipLabel: motivoBloqueio, tooltipPosition: 'left' },
          command: () => opts.onExportarTodas(),
        },
      ],
    },
  ];
}
